/**
 * The transcript as a two-voice conversation (docs/plans/oncall-line-plan.md
 * 1, 5, 7.7, 7.8 rule 3): the caller on the left, staff on the right, each
 * run of one speaker in one bubble with its time in the site's timezone. The
 * server has already merged the call and voicemail sessions and dropped the
 * cross-session duplicates; this only lays them out.
 *
 * The line's own spoken prompts (the greeting, the hold message, the
 * voicemail prompt) travel on the staff track, so they show as Staff (plan
 * 16, WP-04 and WP-05); the note under the heading says so.
 *
 * Every speaker is named in words, not only by side and colour.
 */
import { MessageSquare } from 'lucide-react';
import type { OncallTranscriptSegment } from '../../api/oncallApi';
import { formatTime } from '../../lib/format';
import { TRACK_LABEL, transcriptRuns } from './callMeta';

interface TranscriptViewProps {
  segments: OncallTranscriptSegment[];
  timeZone: string | null | undefined;
}

export default function TranscriptView({ segments, timeZone }: TranscriptViewProps) {
  const runs = transcriptRuns(segments);
  if (runs.length === 0) {
    return (
      <p className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400" data-testid="oncall-transcript-empty">
        <MessageSquare className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
        No transcript was captured for this call.
      </p>
    );
  }
  return (
    <div data-testid="oncall-transcript">
      <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
        Transcribed automatically; it may contain errors. The line&apos;s recorded prompts show as Staff.
      </p>
      <ol className="space-y-2.5">
        {runs.map((run, i) => {
          const caller = run.track === 'caller';
          return (
            <li
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              className={`flex ${caller ? 'justify-start' : 'justify-end'}`}
              data-testid={`oncall-transcript-${run.track}`}
            >
              <div className={`max-w-[85%] ${caller ? '' : 'text-right'}`}>
                <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  {TRACK_LABEL[run.track]}
                  {run.at && <span className="ml-1.5 font-normal normal-case tracking-normal tabular-nums">{formatTime(run.at, timeZone)}</span>}
                </p>
                <div
                  className={`whitespace-pre-wrap break-words rounded-xl px-3 py-2 text-left text-sm ${
                    caller
                      ? 'rounded-tl-sm bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-100'
                      : 'rounded-tr-sm bg-primary-50 text-gray-900 dark:bg-primary-900/30 dark:text-gray-100'
                  }`}
                >
                  {run.lines.join(' ')}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
